import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import LoginPage from './features/users/loginPage';
import UsersPage, { LegacyUsersRedirect, USERS_TAB_PATHS } from './features/users/UsersPage';
import NavBar from './components/NavBar';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './tailwind.css';
import './styles/interaction.css';
import { installTouchCheckCells } from './styles/touchCheckCells';
import './App.css';
import { useAuth } from './contexts/AuthContext';
import { usePermissions } from './hooks/usePermissions';
import QPTracking from './features/quantified-tracking/QPTracking';
import StandingTasksPage from './features/standing-task/pages/StandingTasksPage';
import NotFoundPage from './features/home/pages/NotFoundPage';
import RoutineWorkPage from './features/routine/pages/RoutineWorkPage';
import ProjectWorksPage from './features/projects/pages/ProjectWorksPage';
import AllTasksPage from './features/all-tasks/pages/AllTasksPage';
import ProjectsView from './features/projects/pages/ProjectsView';
import ProjectDetailsPage from './features/projects/pages/ProjectDetailsPage';
import ActivityView from './features/activities/pages/ActivityView';
import ReportingView from './features/reporting/pages/ReportingView';
import FormsView from './features/forms/pages/FormsView';
import { SUBMISSIONS_DRAFTS_PATH } from './features/forms/submissionsPaths';
import { LegacyFormsToApprovals } from './features/forms/pages/legacyFormsToApprovals';
import WorkIdsPage from './features/work-id/pages/WorkIdsPage';
import AreasPage from './features/areas/pages/AreasPage';
import AreasDefaultRedirect from './features/areas/pages/AreasDefaultRedirect';
import AssetsPage from './features/assets/pages/AssetsPage';
import AnimalTypesPage from './features/data-sources/pages/AnimalTypesPage';
import ChrisPropertiesPage from './features/data-sources/pages/ChrisPropertiesPage';
import WarsPage from './features/data-sources/pages/WarsPage';
import RockfallPage from './features/data-sources/pages/RockfallPage';
import BugsnagTestPage from './features/bugsnag-test/pages/BugsnagTestPage';
import EquipmentPage from './features/equipment/pages/EquipmentPage';
import EquipmentDetailPage from './features/equipment/pages/EquipmentDetailPage';
import EquipmentShopBoardPage from './features/equipment/pages/EquipmentShopBoardPage';
import EquipmentDashboardPage from './features/equipment/pages/EquipmentDashboardPage';
import TransfersPage from './features/equipment/pages/TransfersPage';
import TransferDetailPage from './features/equipment/pages/TransferDetailPage';
import VehiclesView from './features/vehicles/pages/VehiclesView';
import VehicleDetailsPage from './features/vehicles/pages/VehicleDetailsPage';
import VehicleCreatePage from './features/vehicles/pages/VehicleCreatePage';
import VehicleProfilesPage from './features/vehicles/pages/VehicleProfilesPage';
import FuelManagementPage from './features/fuel-management/pages/FuelManagementPage';
import PurchaseOrdersPage from './features/purchase-orders/pages/PurchaseOrdersPage';
import PurchaseOrderDetailPage from './features/purchase-orders/pages/PurchaseOrderDetailPage';
import VendorsPage from './features/vendors/pages/VendorsPage';
import VendorDetailPage from './features/vendors/pages/VendorDetailPage';
import HeAdminPage from './features/he-admin/pages/HeAdminPage';
import ResourcesCataloguePage from './features/vendors/pages/ResourcesCataloguePage';
import RedirectPreservingQuery from './components/RedirectPreservingQuery';
import { setRouteDocumentTitle } from './hooks/useDocumentTitle';
import { pageTitleForPath } from './config/pageTitles';
import TasksPage, { TasksIndexRedirect } from './features/tasks/TasksPage';
import HomePage from './features/home/pages/HomePage';
import MessageBoardPage from './features/message-board/pages/MessageBoardPage';
import AuthErrorBoundary from './components/AuthErrorBoundary';
import AccessDenied from './components/AccessDenied';
import { ToastProvider } from './contexts/ToastContext';
import SchedulePage from './features/schedule/SchedulePage';
import PcrSubmissionsDashboardPage from './features/pcr-submissions/pages/PcrSubmissionsDashboardPage';
import { canAccessSubmissionsPage } from './features/forms/utils/submissionPermissions';
import HiredEquipmentGridPage from './features/hired-equipment/pages/HiredEquipmentGridPage';
import LocationsPage from './features/locations/pages/LocationsPage';
import SiteDetailPage from './features/locations/pages/SiteDetailPage';
import LedgerPage from './features/locations/pages/LedgerPage';
import CataloguePage from './features/locations/pages/CataloguePage';
import CountsPage from './features/locations/pages/CountsPage';
import CountSessionPage from './features/locations/pages/CountSessionPage';


interface UserInfo {
  name?: string;
  email?: string;
}

// Layout component that includes NavBar
const Layout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const showNavBar = location.pathname !== '/login';

  // V3-043: every page names itself in the browser tab, "[Page] – DRM WRAP[ – Environment]".
  // Runs after the page's own effects, so a page-specific title (useDocumentTitle) still wins.
  useEffect(() => {
    setRouteDocumentTitle(pageTitleForPath(location.pathname));
  }, [location.pathname]);

  return showNavBar ? (
    // Flex column: the navbar sits in normal flow (its real height, no magic number)
    // and the content area fills the rest. Default: this area scrolls. Pages that need
    // a freeze-pane layout (tables, gantt) still use h-full + overflow-hidden and an
    // inner overflow-y-auto so they fill the pane instead of growing the page.
    // `wrap-app-shell` is 100dvh (with a 100vh fallback) rather than tw-h-screen: on a phone
    // 100vh is the tallest viewport, so the last rows of a page sat under the browser chrome
    // with no scroll left to reach them.
    <div className="wrap-app-shell tw-flex tw-flex-col tw-overflow-hidden">
      <NavBar />
      {/* pb-safe-b keeps the final row clear of the home indicator on a notched device. */}
      <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-y-auto tw-pb-safe-b">
        {children}
      </div>
    </div>
  ) : (
    <>{children}</>
  );
};

// Protected Route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();
  
  // Consistent environment detection
  const env = process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'development';
  const normalizedEnv = env.toLowerCase();
  const validEnvironments = ['development', 'staging', 'training', 'testing', 'production'];
  const releaseStage = validEnvironments.includes(normalizedEnv) ? normalizedEnv : 'development';

  // Temporary development bypass - remove this in production
  if (releaseStage === 'development') {
    return <>{children}</>;
  }

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
};

// Route guard: at least one Submissions/Forms read permission (RB / Mechanic / Hired Equipment)
const FormsRouteGuard = ({ children }: { children: React.ReactNode }) => {
  const { loading } = useAuth();
  const { hasPermission } = usePermissions();
  
  if (loading) {
    return <div>Loading...</div>;
  }

  if (!canAccessSubmissionsPage(hasPermission)) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

// Route guard for Quantified Tasks page
const QPTrackingRouteGuard = ({ children }: { children: React.ReactNode }) => {
  const { loading } = useAuth();
  const { hasPermission } = usePermissions();
  
  if (loading) {
    return <div>Loading...</div>;
  }

  // Check if user has any permission to view quantified tasks
  if (!hasPermission('quantified:task_create') && 
      !hasPermission('quantified:view') && 
      !hasPermission('quantified:sign_work_only')) {
    return <Navigate to="/reports" replace />;
  }

  return <>{children}</>;
};

/**
 * Generic permission guard for a route.
 *
 * The screens below (Users, Areas, Assets, Vehicles, Activities, the admin pages) were wrapped
 * only in ProtectedRoute - i.e. authentication, no authorization. The NavBar already hides them
 * from roles that lack the keys, so they looked protected, but anyone could reach them by
 * typing the URL. They would then render and fail piecemeal as each API call was refused, which
 * reads as "the app is broken" rather than "you don't have access".
 *
 * `anyOf` mirrors the same keys the NavBar uses for each item, so URL navigation and nav
 * visibility now agree.
 */
const RequirePermission = ({
  anyOf,
  what,
  children,
}: {
  anyOf: string[];
  what?: string;
  children: React.ReactNode;
}) => {
  const { loading } = useAuth();
  const { hasPermission } = usePermissions();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!anyOf.some((key) => hasPermission(key))) {
    return <AccessDenied what={what} />;
  }

  return <>{children}</>;
};

// Route guard for Work IDs page
const WorkIdsRouteGuard = ({ children }: { children: React.ReactNode }) => {
  const { loading } = useAuth();
  const { hasPermission } = usePermissions();
  
  if (loading) {
    return <div>Loading...</div>;
  }

  if (!hasPermission('workId:view')) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

// Route guard for Routine Work page
const RoutineWorkRouteGuard = ({ children }: { children: React.ReactNode }) => {
  const { loading } = useAuth();
  const { hasPermission } = usePermissions();
  
  if (loading) {
    return <div>Loading...</div>;
  }

  // Check if user has any permission to view routine tasks
  if (!hasPermission('routine:specific_create') && !hasPermission('routine:view')) {
    return <Navigate to="/reports" replace />;
  }

  return <>{children}</>;
};

// Route guard for Project Works page
const ProjectWorksRouteGuard = ({ children }: { children: React.ReactNode }) => {
  const { loading } = useAuth();
  const { hasPermission } = usePermissions();

  if (loading) {
    return <div>Loading...</div>;
  }

  // Project Works shows tasks that belong to projects.
  if (!hasPermission('project:task_edit') && !hasPermission('project:task_create') && !hasPermission('project:view')) {
    return <Navigate to="/reports" replace />;
  }

  return <>{children}</>;
};

// Route guard for All Tasks page (quantified, routine, and project tasks)
const AllTasksRouteGuard = ({ children }: { children: React.ReactNode }) => {
  const { loading } = useAuth();
  const { hasPermission } = usePermissions();

  if (loading) {
    return <div>Loading...</div>;
  }

  const canAccess =
    hasPermission('quantified:task_create') ||
    hasPermission('quantified:view') ||
    hasPermission('quantified:sign_work_only') ||
    hasPermission('routine:specific_create') ||
    hasPermission('routine:view') ||
    hasPermission('project:task_edit') ||
    hasPermission('project:task_create') ||
    hasPermission('project:view');

  if (!canAccess) {
    return <Navigate to="/reports" replace />;
  }

  return <>{children}</>;
};

// Route guard for Schedule page – requires schedule:view permission
const ScheduleRouteGuard = ({ children }: { children: React.ReactNode }) => {
  const { loading } = useAuth();
  const { hasPermission } = usePermissions();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!hasPermission('schedule:view')) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

// Route guard for PCR page
const PcrRouteGuard = ({ children }: { children: React.ReactNode }) => {
  const { loading } = useAuth();
  const { hasPermission } = usePermissions();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!hasPermission('submission:view') && !hasPermission('pcr:create')) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

// Default redirect component that checks user permissions and role
const DefaultRedirect = () => {
  const { loading } = useAuth();
  const { hasPermission } = usePermissions();
  
  if (loading) {
    return <div>Loading...</div>;
  }

  if (canAccessSubmissionsPage(hasPermission)) {
    return <Navigate to="/submissions" replace />;
  }

  // Users with task permissions default to Quantified Tasks
  if (hasPermission('quantified:task_create') || hasPermission('quantified:view')) {
    return <Navigate to="/tasks/quantified" replace />;
  }

  // Default to Reports for users with report access
  if (
    hasPermission('report:view') ||
    hasPermission('report:material_usage_create') ||
    hasPermission('report:labour_submitted_time_create') ||
    hasPermission('report:finance_monthend_create')
  ) {
    return <Navigate to="/reports" replace />;
  }

  // Fallback to Reports
  return <Navigate to="/reports" replace />;
};

function App() {
  // W-iP7: on touch, a tap anywhere in a `.wrap-touch-check` table cell toggles its checkbox.
  useEffect(() => installTouchCheckCells(), []);
  return (
    <AuthErrorBoundary>
      <ToastProvider>
      <Layout>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
         
          {/* V3-042: Employees, Classifications & Rates and Roles & Permissions each have their own
              route matching the menu label and page title; /users?tab=... redirects. */}
          <Route path="/users" element={<LegacyUsersRedirect />} />
          {(['employees', 'classifications', 'roles'] as const).map((tab) => (
            <Route
              key={tab}
              path={USERS_TAB_PATHS[tab]}
              element={
                <ProtectedRoute>
                  <RequirePermission anyOf={['user:create', 'employee:view', 'classification:view', 'role:view']} what="People & Access">
                    <UsersPage tab={tab} />
                  </RequirePermission>
                </ProtectedRoute>
              }
            />
          ))}
          {/* V2-036: the four task lists are views of one Tasks page. Old routes keep their query. */}
          <Route path="/tasks" element={<ProtectedRoute><TasksIndexRedirect /></ProtectedRoute>} />
          <Route
            path="/tasks/:view"
            element={
              <ProtectedRoute>
                <TasksPage
                  renderView={(view) =>
                    view === 'quantified' ? (
                      <QPTrackingRouteGuard>
                        <QPTracking />
                      </QPTrackingRouteGuard>
                    ) : view === 'routine' ? (
                      <RoutineWorkRouteGuard>
                        <RoutineWorkPage />
                      </RoutineWorkRouteGuard>
                    ) : view === 'project' ? (
                      <ProjectWorksRouteGuard>
                        <ProjectWorksPage />
                      </ProjectWorksRouteGuard>
                    ) : (
                      <AllTasksRouteGuard>
                        <AllTasksPage />
                      </AllTasksRouteGuard>
                    )
                  }
                />
              </ProtectedRoute>
            }
          />
          <Route path="/qp-tracking" element={<RedirectPreservingQuery to="/tasks/quantified" />} />
          <Route 
            path="/standing-tasks" 
            element={
              <ProtectedRoute>
                <StandingTasksPage />
              </ProtectedRoute>
            } 
          />
          <Route path="/routine-work" element={<RedirectPreservingQuery to="/tasks/routine" />} />
          <Route path="/project-works" element={<RedirectPreservingQuery to="/tasks/project" />} />
          <Route path="/all-tasks" element={<RedirectPreservingQuery to="/tasks/all" />} />
          <Route 
            path="/schedule" 
            element={
              <ProtectedRoute>
                <ScheduleRouteGuard>
                  <SchedulePage />
                </ScheduleRouteGuard>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/projects" 
            element={
              <ProtectedRoute>
                <ProjectsView />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/projects/:id" 
            element={
              <ProtectedRoute>
                <ProjectDetailsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/activities" 
            element={
              <ProtectedRoute>
                <RequirePermission anyOf={['activity:create']} what="Activities">
                  <ActivityView />
                </RequirePermission>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/reports" 
            element={
              <ProtectedRoute>
                <ReportingView />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/submissions"
            element={
              <ProtectedRoute>
                <FormsRouteGuard>
                  <FormsView />
                </FormsRouteGuard>
              </ProtectedRoute>
            }
          />
          {/* L1: the submissions queue was /approvals. Old links and bookmarks keep their query. */}
          <Route path="/approvals" element={<RedirectPreservingQuery to="/submissions" />} />
          <Route
            path="/forms"
            element={
              <ProtectedRoute>
                <FormsRouteGuard>
                  {/* V2-038: Forms left the menu; hired equipment drafts are a Submissions tab. */}
                  <LegacyFormsToApprovals>
                    <Navigate to={SUBMISSIONS_DRAFTS_PATH} replace />
                  </LegacyFormsToApprovals>
                </FormsRouteGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/hired-equipment/grid"
            element={
              <ProtectedRoute>
                <FormsRouteGuard>
                  <HiredEquipmentGridPage />
                </FormsRouteGuard>
              </ProtectedRoute>
            }
          />
          {/* V2-043: menu label = page title = route. Old routes keep their query. */}
          <Route path="/pcr-submissions" element={<RedirectPreservingQuery to="/pcrs" />} />
          <Route
            path="/pcrs"
            element={
              <ProtectedRoute>
                <PcrRouteGuard>
                  <PcrSubmissionsDashboardPage />
                </PcrRouteGuard>
              </ProtectedRoute>
            }
          />
          <Route 
            path="/work-ids" 
            element={
              <ProtectedRoute>
                <WorkIdsRouteGuard>
                  <WorkIdsPage />
                </WorkIdsRouteGuard>
              </ProtectedRoute>
            } 
          />
          <Route
            path="/areas/:section"
            element={
              <ProtectedRoute>
                <RequirePermission anyOf={['area:service_view', 'area:foreman_view', 'area:contract_view']} what="Areas">
                  <AreasPage />
                </RequirePermission>
              </ProtectedRoute>
            }
          />
          <Route
            path="/areas"
            element={
              <ProtectedRoute>
                <RequirePermission anyOf={['area:service_view', 'area:foreman_view', 'area:contract_view']} what="Areas">
                  <AreasDefaultRedirect />
                </RequirePermission>
              </ProtectedRoute>
            }
          />
          <Route 
            path="/assets" 
            element={
              <ProtectedRoute>
                <RequirePermission anyOf={['asset:view']} what="Assets">
                  <AssetsPage />
                </RequirePermission>
              </ProtectedRoute>
            } 
          />
          <Route
            path="/admin/data-sources/animal-types"
            element={
              <ProtectedRoute>
                <RequirePermission anyOf={['data_sources:view']} what="Reference Data">
                  <AnimalTypesPage />
                </RequirePermission>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/data-sources/chris-properties"
            element={
              <ProtectedRoute>
                <RequirePermission anyOf={['data_sources:view']} what="Reference Data">
                  <ChrisPropertiesPage />
                </RequirePermission>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/data-sources/wars"
            element={
              <ProtectedRoute>
                <RequirePermission anyOf={['data_sources:view']} what="Reference Data">
                  <WarsPage />
                </RequirePermission>
              </ProtectedRoute>
            }
          />
          <Route path="/fuel" element={<RedirectPreservingQuery to="/admin/fuel-surcharge" />} />
          <Route
            path="/admin/data-sources/rockfall"
            element={
              <ProtectedRoute>
                <RequirePermission anyOf={['data_sources:view']} what="Reference Data">
                  <RockfallPage />
                </RequirePermission>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/fuel-surcharge"
            element={
              <ProtectedRoute>
                <RequirePermission anyOf={['fuel_management:view']} what="Fuel Surcharge">
                  <FuelManagementPage />
                </RequirePermission>
              </ProtectedRoute>
            }
          />
          <Route path="/admin/fuel-management" element={<RedirectPreservingQuery to="/admin/fuel-surcharge" />} />
          <Route 
            path="/test-bugsnag" 
            element={
              <ProtectedRoute>
                <BugsnagTestPage />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/equipment"
            element={
              <ProtectedRoute>
                <RequirePermission anyOf={['vehicle:view']} what="Equipment">
                  <EquipmentPage />
                </RequirePermission>
              </ProtectedRoute>
            }
          />
          <Route
            path="/equipment/board"
            element={
              <ProtectedRoute>
                <RequirePermission anyOf={['vehicle:view']} what="Equipment">
                  <EquipmentShopBoardPage />
                </RequirePermission>
              </ProtectedRoute>
            }
          />
          <Route
            path="/equipment/dashboard"
            element={
              <ProtectedRoute>
                <RequirePermission anyOf={['vehicle:view']} what="Equipment">
                  <EquipmentDashboardPage />
                </RequirePermission>
              </ProtectedRoute>
            }
          />
          {/* Finance completes transfers without holding any vehicle key, so these take the transfer keys too. */}
          <Route
            path="/equipment/transfers"
            element={
              <ProtectedRoute>
                <RequirePermission anyOf={['vehicle:view', 'fleet.transferServiceArea', 'equipment:transfer_complete']} what="Equipment transfers">
                  <TransfersPage />
                </RequirePermission>
              </ProtectedRoute>
            }
          />
          <Route
            path="/equipment/transfers/:transferId"
            element={
              <ProtectedRoute>
                <RequirePermission anyOf={['vehicle:view', 'fleet.transferServiceArea', 'equipment:transfer_complete']} what="Equipment transfers">
                  <TransferDetailPage />
                </RequirePermission>
              </ProtectedRoute>
            }
          />
          <Route
            path="/equipment/:id"
            element={
              <ProtectedRoute>
                <RequirePermission anyOf={['vehicle:view']} what="Equipment">
                  <EquipmentDetailPage />
                </RequirePermission>
              </ProtectedRoute>
            }
          />
          <Route 
            path="/vehicles" 
            element={
              <ProtectedRoute>
                <RequirePermission anyOf={['vehicle:view']} what="Vehicles">
                  <VehiclesView />
                </RequirePermission>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/vehicles/new" 
            element={
              <ProtectedRoute>
                <VehicleCreatePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/vehicles/:id" 
            element={
              <ProtectedRoute>
                <VehicleDetailsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/vehicle-profiles" 
            element={
              <ProtectedRoute>
                <VehicleProfilesPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/purchase-orders" 
            element={
              <ProtectedRoute>
                <RequirePermission anyOf={['po:view']} what="Purchase Orders">
                  <PurchaseOrdersPage />
                </RequirePermission>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/purchase-orders/:id" 
            element={
              <ProtectedRoute>
                <RequirePermission anyOf={['po:view']} what="Purchase Orders">
                  <PurchaseOrderDetailPage />
                </RequirePermission>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/vendors" 
            element={
              <ProtectedRoute>
                <RequirePermission anyOf={['heMaster:vendor_view']} what="Vendors">
                  <VendorsPage />
                </RequirePermission>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/vendors/:id" 
            element={
              <ProtectedRoute>
                <RequirePermission anyOf={['heMaster:vendor_view']} what="Vendors">
                  <VendorDetailPage />
                </RequirePermission>
              </ProtectedRoute>
            } 
          />
          <Route
            path="/vendor-approvals"
            element={
              <ProtectedRoute>
                <RequirePermission anyOf={['heMaster:vendor_approve']} what="Vendor Approvals">
                  <HeAdminPage />
                </RequirePermission>
              </ProtectedRoute>
            }
          />
          {/* L1: renamed from Vendor Admin (/he-admin). */}
          <Route path="/he-admin" element={<RedirectPreservingQuery to="/vendor-approvals" />} />
          <Route
            path="/resources"
            element={
              <ProtectedRoute>
                {/* Same gate as the Vendors list; the catalogue was a tab of Vendor Admin. */}
                <RequirePermission anyOf={['heMaster:vendor_view']} what="Resources">
                  <ResourcesCataloguePage />
                </RequirePermission>
              </ProtectedRoute>
            }
          />
          {/*
            The subcontractor master screens are superseded by /vendors (HE-M01 collapsed the
            two lists into one vendor database). The old paths redirect rather than 404 so
            existing bookmarks and links keep working.
          */}
          <Route path="/subcontractors-master" element={<Navigate to="/vendors" replace />} />
          <Route path="/subcontractors-master/:id" element={<Navigate to="/vendors" replace />} />
          {/* V3-041: Material Locations lives at /material-locations; every old /locations URL
              redirects, keeping the rest of the path and the query (site, material, filters). */}
          <Route path="/locations" element={<RedirectPreservingQuery to="/material-locations" />} />
          <Route path="/locations/*" element={<RedirectPreservingQuery to="/material-locations" fromPrefix="/locations" />} />
          <Route
            path="/material-locations"
            element={
              <ProtectedRoute>
                <LocationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/material-locations/ledger"
            element={
              <ProtectedRoute>
                <LedgerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/material-locations/catalogue"
            element={
              <ProtectedRoute>
                <CataloguePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/material-locations/counts"
            element={
              <ProtectedRoute>
                <CountsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/material-locations/counts/:countId"
            element={
              <ProtectedRoute>
                <CountSessionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/material-locations/:siteId"
            element={
              <ProtectedRoute>
                <SiteDetailPage />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/home" 
            element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            } 
          />
          {/* V3-042: Message Center at /message-center; the old /message-board redirects. */}
          <Route path="/message-board" element={<RedirectPreservingQuery to="/message-center" />} />
          <Route
            path="/message-center"
            element={
              <ProtectedRoute>
                <RequirePermission anyOf={['message_board:view', 'message_board:post']} what="Message Center">
                  <MessageBoardPage />
                </RequirePermission>
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/home" replace />} />
          {/* Unmatched routes render a real 404. Redirecting to /home instead made every
              broken link look like a normal visit, which is how a dead nav item survived
              unnoticed — see NotFoundPage. */}
          <Route
            path="*"
            element={
              <ProtectedRoute>
                <NotFoundPage />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Layout>
      </ToastProvider>
    </AuthErrorBoundary>
  );
}

export default App; 